import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Save, X, AlertCircle } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';

// Unit creation/edit form.
//
// Architecture A (locked in 6cecebc): unit_types is the canonical rate
// source; units inherit. So this form REQUIRES picking a unit_type on
// create — no more free-text size + arbitrary monthly_rate.
//
// On edit: legacy units that pre-date the catalog (no unit_type_id) are
// still editable for status / notes / number without being forced to
// pick a type retroactively. If the operator DOES pick a type during an
// edit, we update unit_type_id + unit_type (display cache) and pre-fill
// the rate from the catalog (still editable as a per-unit override).
const UnitForm = () => {
  const { id } = useParams();
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [unitTypes, setUnitTypes] = useState([]);
  const [error, setError] = useState(null);
  // Tracks whether the rate field currently represents a true per-unit
  // override (vs a pre-filled inherited value). Cleared when the type
  // picker pre-fills, set true when the operator edits the field. We
  // only PUT rate_override on edit if this is true — otherwise an
  // un-edited form would accidentally promote an inherited rate to a
  // locked override.
  const [rateIsOverride, setRateIsOverride] = useState(false);
  const [formData, setFormData] = useState({
    unit_number: '',
    unit_type_id: '',
    unit_type: '',          // legacy display cache — populated from picked type
    status: 'Available',
    monthly_rate: '',       // empty = inherit from type; non-empty = override
    notes: '',
  });

  // Pull the facility's unit_types catalog up front. Required before the
  // operator can submit (create) or change the type (edit).
  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    (async () => {
      setLoadingTypes(true);
      try {
        const res = await apiClient.get(
          `/api/facilities/${facilityId}/unit-types/`
        );
        if (!cancelled) setUnitTypes(res.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load unit types:', err);
          setError('Could not load unit types for this facility.');
        }
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [facilityId]);

  const fetchUnit = useCallback(async () => {
    if (!id || !facilityId) return;
    try {
      const response = await apiClient.get(
        `/api/facilities/${facilityId}/units/${id}`
      );
      const u = response.data || {};
      const hasOverride = u.rate_override != null;
      setRateIsOverride(hasOverride);
      setFormData({
        unit_number: u.unit_number || '',
        unit_type_id: u.unit_type_id || '',
        unit_type: u.unit_type || '',
        status: u.status || 'Available',
        // Show the EFFECTIVE rate (override or inherited) for context,
        // but rateIsOverride remembers which one so we don't promote an
        // inherited value to a locked override on a no-op save.
        monthly_rate: hasOverride
          ? String(u.rate_override)
          : (u.monthly_rate != null ? String(u.monthly_rate) : ''),
        notes: u.notes || '',
      });
    } catch (err) {
      console.error('Error fetching unit:', err);
      setError('Could not load this unit.');
    }
  }, [id, facilityId]);

  useEffect(() => { if (id) fetchUnit(); }, [id, fetchUnit]);

  const handleChange = (e) => {
    if (e.target.name === 'monthly_rate') {
      setRateIsOverride(true);
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // When operator picks a unit_type, mirror the type's name into the
  // legacy `unit_type` display field AND pre-fill monthly_rate from the
  // catalog. The rate stays editable — non-empty = per-unit override,
  // empty = inherit on render.
  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    const selectedType = unitTypes.find((t) => t._id === typeId);
    // Picking a type re-baselines the rate to the catalog value — it
    // stops being an override until the operator edits it again.
    setRateIsOverride(false);
    setFormData({
      ...formData,
      unit_type_id: typeId,
      unit_type: selectedType?.name || '',
      monthly_rate:
        selectedType?.monthly_rate != null
          ? String(selectedType.monthly_rate)
          : '',
    });
  };

  const noTypesDefined = !loadingTypes && unitTypes.length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Guard the create path: catalog must exist and a type must be picked.
    if (!isEdit) {
      if (noTypesDefined) {
        setError('Define a unit type first.');
        return;
      }
      if (!formData.unit_type_id) {
        setError('Pick a unit type.');
        return;
      }
    }

    setLoading(true);
    try {
      const rateRaw = String(formData.monthly_rate).trim();
      const rateNum = rateRaw === '' ? null : parseFloat(rateRaw);

      const payload = {
        unit_number: formData.unit_number,
        status: formData.status,
        notes: formData.notes || null,
      };
      if (formData.unit_type_id) {
        payload.unit_type_id = formData.unit_type_id;
        payload.unit_type = formData.unit_type;
      } else if (isEdit && formData.unit_type) {
        // Legacy unit being edited without picking a type — preserve the
        // old free-text label so display layers don't break.
        payload.unit_type = formData.unit_type;
      }
      // Rate semantics:
      //  - create: send monthly_rate (backend stores as the unit's rate,
      //    falls back to the type's rate if blank/zero).
      //  - edit: only send rate_override if the operator actually edited
      //    the rate field (rateIsOverride=true). Blank-after-edit means
      //    clear the override and fall back to the type's catalog rate.
      //    Untouched field → don't send anything, preserving whatever
      //    server-side state (override or inherited) was already there.
      if (isEdit) {
        if (rateIsOverride) {
          payload.rate_override = rateNum;
        }
      } else if (rateNum != null && !Number.isNaN(rateNum)) {
        payload.monthly_rate = rateNum;
      }

      if (isEdit) {
        await apiClient.put(
          `/api/facilities/${facilityId}/units/${id}`, payload
        );
        // Stay on the unit being edited.
        toast.success('Unit saved.');
      } else {
        await apiClient.post(
          `/api/facilities/${facilityId}/units/`, payload
        );
        navigate('/units');
      }
    } catch (err) {
      console.error('Error saving unit:', err);
      setError(
        err?.response?.data?.detail || 'Failed to save unit.'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:bg-slate-50 disabled:text-slate-400';
  const labelClass = 'block text-[13px] font-medium text-slate-700 mb-1.5';

  // Block the form entirely when there are no unit types and we're
  // creating a new unit. Operator must define a type first — that's the
  // ESS-pattern catalog-first workflow.
  const formDisabled = !isEdit && noTypesDefined;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">
          {isEdit ? 'Edit Unit' : 'New Unit'}
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          {isEdit ? 'Update unit details' : 'Add a new storage unit'}
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-[13px] text-red-600 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!isEdit && noTypesDefined && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          <p className="font-medium mb-1">Define unit types first</p>
          <p className="text-amber-700">
            This facility has no unit types defined yet. Unit types are
            the rate sheet — each unit inherits its monthly rate from
            its type.{' '}
            <Link
              to="/units?view=types"
              className="font-medium underline hover:text-amber-900"
            >
              Manage unit types →
            </Link>
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        <form onSubmit={handleSubmit} className="space-y-5">
          <fieldset disabled={formDisabled} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Unit Number *</label>
                <input
                  type="text" name="unit_number" required
                  value={formData.unit_number} onChange={handleChange}
                  className={inputClass} placeholder="e.g., A-101"
                />
              </div>
              <div>
                <label className={labelClass}>
                  Unit Type {!isEdit && '*'}
                </label>
                <select
                  name="unit_type_id"
                  required={!isEdit}
                  value={formData.unit_type_id}
                  onChange={handleTypeChange}
                  className={inputClass}
                >
                  <option value="">
                    {loadingTypes
                      ? 'Loading types…'
                      : (isEdit
                          ? `— keep current${formData.unit_type ? ` (${formData.unit_type})` : ''} —`
                          : '— select a unit type —')}
                  </option>
                  {unitTypes.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name} — ${Number(t.monthly_rate).toFixed(2)}/mo
                    </option>
                  ))}
                </select>
                {isEdit && !formData.unit_type_id && formData.unit_type && (
                  <p className="text-[12px] text-slate-400 mt-1">
                    Legacy unit (type:{' '}
                    <span className="font-mono">{formData.unit_type}</span>).
                    Picking a type from the catalog will link it.
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>
                  Monthly Rate{' '}
                  <span className="text-slate-400 font-normal">
                    (defaults to type rate)
                  </span>
                </label>
                <input
                  type="number" name="monthly_rate" step="0.01"
                  value={formData.monthly_rate} onChange={handleChange}
                  className={inputClass}
                  placeholder={isEdit ? 'blank = inherit from type' : '0.00'}
                />
                <p className="text-[12px] text-slate-400 mt-1">
                  {isEdit
                    ? 'Override the catalog rate for this specific unit. Leave blank to fall back to the type.'
                    : 'Pre-filled from the selected type. Edit to set a per-unit override.'}
                </p>
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select
                  name="status" value={formData.status}
                  onChange={handleChange} className={inputClass}
                >
                  <option value="Available">Available</option>
                  <option value="Occupied">Occupied</option>
                  <option value="Reserved">Reserved</option>
                  <option value="Maintenance">Maintenance</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                name="notes" value={formData.notes}
                onChange={handleChange} rows="3" className={inputClass}
              />
            </div>
          </fieldset>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button" onClick={() => navigate('/units')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button
              type="submit" disabled={loading || formDisabled}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-3.5 w-3.5" />
              {loading ? 'Saving…' : 'Save Unit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnitForm;
